import React, { useEffect, useState } from "react";

// Helper: Fetch event hours (regular + special)
const fetchEventHours = async (productId, variantId) => {
  const res = await fetch(
    `https://servicify-appointments.herokuapp.com/api/products/${productId}/events?variantId=${variantId}`
  );
  const data = await res.json();
  // console.log(data);
  const employee = data.employees?.[0];
  if (!employee) {
    console.warn(
      `⚠️ No employee found for event: ${data.name} (id: ${data.id})`
    );
    return { regularHours: [], specialHours: [] };
  }

  return {
    regularHours: employee.regularHours,
    specialHours: employee.specialHours,
  };
};

// Helper: Fetch bookings for a specific employee on a date
const fetchBookings = async (eventId, employeeId, date) => {
  const res = await fetch(
    `https://servicify-appointments.herokuapp.com/api/events/${eventId}/employees/busy-slots`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        employeeIds: [employeeId],
        dateFrom: date,
        dateTo: date,
        inputTimezone: "Europe/London",
      }),
    }
  );
  return await res.json();
};

// Helper: Generate slot list from ranges
const generateSlotsFromHours = (ranges) => {
  if (!ranges || !Array.isArray(ranges)) return [];
  const slots = [];

  ranges.forEach((range) => {
    if (!range.includes("-")) return;
    const [start, end] = range.split("-");
    const startHour = parseInt(start.split(":")[0], 10);
    const endHour = parseInt(end.split(":")[0], 10);
    for (let h = startHour; h < endHour; h++) {
      slots.push(`${h.toString().padStart(2, "0")}:00`);
    }
  });

  return slots;
};

const SlotList = ({
  date,
  locationName,
  productId,
  peak,
  offpeak,
  privateConfig,
  maxCapacity,
}) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const updateSlots = async () => {
      setLoading(true);

      const weekday = new Date(date).toLocaleDateString("en-GB", {
        weekday: "long",
      });

      const [peakHours, offpeakHours] = await Promise.all([
        fetchEventHours(productId, peak.variantId),
        fetchEventHours(productId, offpeak.variantId),
      ]);
      // console.log("Peak hours for ", productId, " are ", peakHours);
      // console.log("Offpeak hours for ", productId, " are ", offpeakHours);

      const peakRanges = Array.isArray(peakHours.specialHours?.[date])
        ? peakHours.specialHours[date]
        : Array.isArray(peakHours.regularHours?.[weekday])
        ? peakHours.regularHours[weekday]
        : [];

      const offpeakRanges = Array.isArray(offpeakHours.specialHours?.[date])
        ? offpeakHours.specialHours[date]
        : Array.isArray(offpeakHours.regularHours?.[weekday])
        ? offpeakHours.regularHours[weekday]
        : [];

      const peakSlotList = generateSlotsFromHours(peakRanges);
      const offPeakSlotList = generateSlotsFromHours(offpeakRanges);
      // console.log("Peak slot list", peakSlotList);
      // console.log("Off-peak slot list", offPeakSlotList);

      const allSlots = [
        ...new Set([...peakSlotList, ...offPeakSlotList]),
      ].sort();
      const slotTypeMap = {};
      peakSlotList.forEach((time) => {
        if (!slotTypeMap[time]) slotTypeMap[time] = [];
        slotTypeMap[time].push("peak");
      });
      offPeakSlotList.forEach((time) => {
        if (!slotTypeMap[time]) slotTypeMap[time] = [];
        slotTypeMap[time].push("offpeak");
      });

      const [peakData, offpeakData] = await Promise.all([
        fetchBookings(peak.eventId, peak.employeeId, date),
        fetchBookings(offpeak.eventId, offpeak.employeeId, date),
      ]);

      // 🔐 Private booking support
      let privateTimes = new Set();
      // if (
      //   privateConfig?.eventId &&
      //   privateConfig?.employeeId &&
      //   privateConfig?.variantId
      // ) {
      //   const privateData = await fetchBookings(
      //     privateConfig.eventId,
      //     privateConfig.employeeId,
      //     date
      //   );
      //   const privateBookings =
      //     privateData.bookings?.[privateConfig.employeeId] || [];

      //   privateBookings.forEach((b) => {
      //     const bookingDate = new Date(b.date.startTimeString);
      //     const bookingDateLocal = bookingDate.toLocaleDateString("en-CA", {
      //       timeZone: "Europe/London",
      //     });
      //     if (bookingDateLocal !== date) return;

      //     const time = bookingDate.toLocaleTimeString("en-GB", {
      //       hour: "2-digit",
      //       minute: "2-digit",
      //       hour12: false,
      //       timeZone: "Europe/London",
      //     });
      //     privateTimes.add(time);
      //   });
      // }

      const combinedBookings = {};
      [
        [peakData.bookings[peak.employeeId], false],
        [offpeakData.bookings[offpeak.employeeId], true],
      ].forEach(([bookings, isOffPeak, isPrivate]) => {
        if (!bookings) return;

        bookings.forEach((b) => {
          const bookingDate = new Date(b.date.startTimeString);
          const bookingDateLocal = bookingDate.toLocaleDateString("en-CA", {
            timeZone: "Europe/London",
          });

          if (bookingDateLocal !== date) return;

          const time = bookingDate.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Europe/London",
          });

          if (
            b.variantId === "54598688932185" ||
            b.variantId === "54598689554777"
          ) {
            privateTimes.add(time);
            console.log(privateTimes);
          }

          if (!combinedBookings[time]) {
            combinedBookings[time] = {
              count: 0,
              quantities: [],
              isOffPeak,
              isPrivate,
            };
          }

          combinedBookings[time].count += b.quantity;
          combinedBookings[time].quantities.push(b.quantity);
        });
      });

      const mergedList = allSlots.map((time) => {
        const types = slotTypeMap[time];
        const isOffPeak = types?.includes("offpeak");
        const booking = combinedBookings[time];
        const count = booking?.count || 0;
        const isPrivate = privateTimes.has(time);
        const currentDateTime = new Date();
        const slotDateTime = new Date(`${date}T${time}`);
        const timePassed = slotDateTime <= currentDateTime;

        return {
          time,
          isOffPeak,
          isPrivate,
          available: isPrivate ? 0 : maxCapacity - count,
          timePassed,
          quantities: booking?.quantities || [],
        };
      });
      // console.log("Merged slot list", mergedList);
      setSlots(mergedList);
      setLoading(false);
    };

    updateSlots();
  }, [date, productId, peak, offpeak, privateConfig, maxCapacity]);

  const formatTime = (time) => {
    const [hourStr] = time.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
    return `${hour}${ampm}`;
  };

  return (
    <div>
      <h3>{locationName}</h3>
      {loading ? (
        <p>Loading...</p>
      ) : slots.length === 0 ? (
        <p style={{ textAlign: "center" }}>Closed</p>
      ) : (
        <table className="slot-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Booked</th>
              <th>Available</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {slots.map(
              ({
                time,
                isOffPeak,
                isPrivate,
                available,
                timePassed,
                quantities,
              }) => (
                <tr
                  key={time}
                  className={`slot-row 
                  ${timePassed ? "passed" : ""}
                  ${
                    available === 0
                      ? "full"
                      : available < maxCapacity
                      ? "partial"
                      : "available"
                  }
                  ${available > 9 ? "ten" : ""}
                  ${available < 1 ? "zero" : ""}`}
                >
                  <td>{formatTime(time)}</td>
                  <td colSpan={isPrivate ? 2 : 1}>
                    {isPrivate ? (
                      "Private booking"
                    ) : quantities.length ? (
                      <>
                        {quantities.reduce((a, b) => a + b, 0)}
                        <div className="quantities">
                          {quantities.length > 1 && quantities.join(" ")}
                        </div>
                      </>
                    ) : (
                      0
                    )}
                  </td>
                  {!isPrivate && <td>{available > 0 ? available : 0}</td>}
                  <td>
                    {!isPrivate && available > 0 && !timePassed && (
                      <button
                        onClick={() => {
                          const variantId = isOffPeak
                            ? offpeak?.variantId
                            : peak?.variantId;
                          const locationSlug = locationName
                            .toLowerCase()
                            .replace(/ /g, "-");
                          const url = `https://hikitalo.com/products/${locationSlug}?variant=${variantId}`;
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        className={`book-button${isOffPeak ? " offpeak" : ""}`}
                        disabled={available === 0}
                      >
                        {isOffPeak ? "£13" : "£16"}
                      </button>
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SlotList;
